import { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';
import useIsMobile from '../hooks/useIsMobile';
import toast from 'react-hot-toast';

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    + ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

const STATUS_LABELS = { new: 'Nouvelle', treated: 'Traitée' };
const STATUS_COLORS = { new: { bg: '#fef9c3', color: '#854d0e' }, treated: { bg: '#d1fae5', color: '#065f46' } };

export default function ContactSubmissions() {
  const mob = useIsMobile();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/notifications/contact/submissions/');
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const toggleStatus = async (item) => {
    const newStatus = item.status === 'new' ? 'treated' : 'new';
    try {
      await api.patch(`/notifications/contact/submissions/${item.id}/`, { status: newStatus });
      setItems(prev => prev.map(it => it.id === item.id ? { ...it, status: newStatus } : it));
    } catch {
      toast.error('Erreur lors de la mise à jour.');
    }
  };

  return (
    <div style={{ width: '100%' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '1.6rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>
          Demandes de contact
        </h1>
        <p style={{ color: 'var(--text-secondary)', marginTop: '4px', fontSize: '0.9rem' }}>
          Demandes envoyées depuis la page d'accueil (création d'espace, contact général)
        </p>
      </div>

      <div style={{ marginBottom: '16px', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
        {loading ? 'Chargement...' : `${items.length} demande${items.length !== 1 ? 's' : ''}`}
      </div>

      <div style={{ background: 'var(--card-bg)', borderRadius: '12px', border: '1px solid var(--border)', overflowX: 'auto', WebkitOverflowScrolling: 'touch', boxShadow: 'var(--card-shadow)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: mob ? '640px' : 'auto' }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '2px solid var(--border)' }}>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, fontSize: '0.82rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Date</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, fontSize: '0.82rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Type</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, fontSize: '0.82rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Nom</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, fontSize: '0.82rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Contact</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, fontSize: '0.82rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Église / Ville</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, fontSize: '0.82rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Message</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, fontSize: '0.82rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Statut</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>Chargement...</td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>Aucune demande pour le moment</td>
              </tr>
            ) : (
              items.map((item, i) => {
                const st = STATUS_COLORS[item.status] || STATUS_COLORS.new;
                return (
                  <tr key={item.id} style={{ borderBottom: '1px solid var(--border)', background: i % 2 === 0 ? 'transparent' : 'rgba(248,250,252,0.5)' }}>
                    <td style={{ padding: '10px 16px', fontSize: '0.82rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                      {formatDate(item.created_at)}
                    </td>
                    <td style={{ padding: '10px 16px', fontSize: '0.85rem', color: 'var(--text-primary)' }}>
                      {item.subject_type_label}
                    </td>
                    <td style={{ padding: '10px 16px', fontSize: '0.87rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                      {item.name}
                    </td>
                    <td style={{ padding: '10px 16px', fontSize: '0.82rem', color: 'var(--text-primary)' }}>
                      <div>{item.email}</div>
                      {item.phone && <div style={{ color: 'var(--text-secondary)' }}>{item.phone}</div>}
                    </td>
                    <td style={{ padding: '10px 16px', fontSize: '0.85rem', color: 'var(--text-primary)' }}>
                      {item.church_name && <div>{item.church_name}</div>}
                      {item.city && <div style={{ color: 'var(--text-secondary)' }}>{item.city}</div>}
                      {!item.church_name && !item.city && '—'}
                    </td>
                    <td style={{ padding: '10px 16px', fontSize: '0.85rem', color: 'var(--text-primary)', maxWidth: '280px', whiteSpace: 'pre-wrap' }}>
                      {item.message || '—'}
                    </td>
                    <td style={{ padding: '10px 16px' }}>
                      <button onClick={() => toggleStatus(item)}
                        style={{ display: 'inline-block', padding: '3px 10px', borderRadius: '999px', fontSize: '0.78rem', fontWeight: 600, background: st.bg, color: st.color, border: 'none', cursor: 'pointer' }}>
                        {STATUS_LABELS[item.status] || item.status}
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
